import React from "react";

import Typography from "@mui/material/Typography";

import useUpdateClips from "@app/analytics/Home/hooks/useUpdateClips";
import type { KeyQuestionAnswerFeedItem } from "@app/analytics/Home/types";
import { isProposedAnswer } from "@app/analytics/utils";
import { toast } from "@app/common/hooks/useToasts";
import type { KeyQuestionAnswerClipStatus } from "@app/common/types";

import Stack from "@rui/Stack";
import { Bold } from "@rui/styles";

import BaseHomeFeedItemLayout from "./BaseHomeFeedItemLayout";
import { Content } from "./styles";
import ProposedAnswer from "../ProposedAnswer";

interface ConnectedKeyQuestionAnswerItemProps
  extends KeyQuestionAnswerFeedItem {
  onClick: (id: string) => void;
}

type KeyQuestionAnswerItemProps = ConnectedKeyQuestionAnswerItemProps & {
  onUpdateStatus: (status: KeyQuestionAnswerClipStatus) => void;
};

const KeyQuestionAnswerItem = ({
  onClick,
  onUpdateStatus,
  ...item
}: KeyQuestionAnswerItemProps) => {
  const handleAccept = () => onUpdateStatus("user_accept");
  const handleReject = () => onUpdateStatus("user_reject");

  const { data, text } = item;

  return (
    <BaseHomeFeedItemLayout {...item} onClick={onClick}>
      <Stack spacing={1}>
        <Content spacing={1} type={item.type}>
          <Typography variant="bodySecondaryReg">{data.question}</Typography>
          {isProposedAnswer(data.status) ? (
            <ProposedAnswer
              canEdit={data.canEdit}
              onAccept={handleAccept}
              onReject={handleReject}
            >
              {text}
            </ProposedAnswer>
          ) : (
            <Typography variant="bodySecondaryReg">
              <Bold>Answer: </Bold> {text}
            </Typography>
          )}
        </Content>
      </Stack>
    </BaseHomeFeedItemLayout>
  );
};

const ConnectedKeyQuestionAnswerItem = (
  props: ConnectedKeyQuestionAnswerItemProps
) => {
  const updateClip = useUpdateClips();

  const updateAnswerStatus = (status: KeyQuestionAnswerClipStatus) => {
    updateClip.mutate(
      {
        id: props.entityId,
        type: "key_question_answer",
        parentClipId: props.data.questionId,
        sessionId: props.session.id,
        status,
      },
      {
        onError: () => {
          toast.error(
            "Something went wrong and your update could not be saved. Please refresh the page or try again later."
          );
        },
      }
    );
  };

  return (
    <KeyQuestionAnswerItem {...props} onUpdateStatus={updateAnswerStatus} />
  );
};

export { KeyQuestionAnswerItem };
export default ConnectedKeyQuestionAnswerItem;
